// The client half of saved decks and match records.
//
// Everything here needs a Discord access token, because the server establishes
// who you are by asking Discord rather than believing the browser. Outside a
// Discord Activity there is no identity, so every call is a no-op that reports
// "signed out" -- the game still runs, it just has nowhere to keep anything.

import type { DeckList } from '../rules/engine.ts';
import type { PlayerId } from '../rules/types.ts';
import type { Replay } from './log.ts';

/**
 * What the list knows about a saved match without opening it.
 *
 * Every field was established by the server *replaying* the match, not by the
 * client describing it -- so a shared recording cannot be labelled a win it
 * did not win, and a row that is listed is one that will play.
 */
export interface ReplayMeta {
  id: string;
  title: string | null;
  opponent: 'ai' | 'human';
  /** The difficulty, or the other player's name. */
  versus: string | null;
  outcome: 'win' | 'loss' | 'draw' | null;
  /** The legends, which is how a player recognises which deck it was. */
  legend: string | null;
  legendVs: string | null;
  turns: number | null;
  moves: number;
  shared: boolean;
  created: number;
}

/** What the list can be narrowed by. Everything is optional. */
export interface ReplayFilter {
  opponent?: 'ai' | 'human';
  outcome?: 'win' | 'loss' | 'draw';
  legend?: string;
  legendVs?: string;
  q?: string;
  limit?: number;
}

/** A page of saved matches, and the values worth offering as filters. */
export interface ReplayPage {
  replays: ReplayMeta[];
  /** Only legends this player has actually played, on each side. */
  legends: string[];
  against: string[];
}

/** Turns a filter into a query string, leaving out what was not asked for. */
export function replayQuery(filter: ReplayFilter = {}): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

export interface StoredDeck {
  id: string;
  name: string;
  list: DeckList;
  updated: number;
}

export interface Record {
  win: number;
  loss: number;
  draw: number;
}

export interface Stats {
  overall: Record;
  /** Keyed by deck id, for the decks that have played. */
  byDeck: Record_ByDeck;
}

type Record_ByDeck = { [deckId: string]: Record };

export interface Profile {
  user: { id: string; username: string; avatar: string | null };
  stats: Stats;
}

export const EMPTY_RECORD: Record = { win: 0, loss: 0, draw: 0 };

/**
 * Talks to the server on behalf of one signed-in player.
 *
 * Constructed with the access token the Activity handshake produced. Without a
 * token there is no `Profiles` at all -- the caller checks for null rather than
 * every method having to.
 */
export class Profiles {
  private readonly token: string;

  constructor(token: string) {
    this.token = token;
  }

  private async call<T>(
    path: string, init: RequestInit = {},
  ): Promise<T | null> {
    try {
      const res = await fetch(path, {
        ...init,
        headers: {
          ...(init.body ? { 'Content-Type': 'application/json' } : {}),
          Authorization: `Bearer ${this.token}`,
          ...(init.headers ?? {}),
        },
      });
      if (!res.ok) {
        console.warn(`riftbound api ${path}: ${res.status}`);
        return null;
      }
      return await res.json() as T;
    } catch (err) {
      // The match must survive the server being unreachable; saved decks are a
      // convenience, not a dependency.
      console.warn(`riftbound api ${path} failed:`, err);
      return null;
    }
  }

  me(): Promise<Profile | null> {
    return this.call<Profile>('/api/me');
  }

  async decks(): Promise<StoredDeck[]> {
    const got = await this.call<{ decks: StoredDeck[] }>('/api/decks');
    return got?.decks ?? [];
  }

  /** Saves a deck, returning its id. Pass `id` to overwrite an existing one. */
  async saveDeck(name: string, list: DeckList, id?: string): Promise<string | null> {
    const got = await this.call<{ id: string }>('/api/decks', {
      method: 'POST',
      body: JSON.stringify({ id, name, list }),
    });
    return got?.id ?? null;
  }

  async deleteDeck(id: string): Promise<boolean> {
    return (await this.call<{ ok: true }>(`/api/decks/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    })) !== null;
  }

  // -- recorded matches ------------------------------------------------------

  /**
   * Keeps a finished match so it can be watched again.
   *
   * Only the replay is sent that matters: the server runs it and derives who
   * won, how long it went and which legends played, so the rest of this is
   * only what the server cannot know -- whether the opponent was a person, and
   * what to call them.
   */
  async saveReplay(
    replay: Replay, detail: { opponent: 'ai' | 'human'; versus?: string; title?: string },
  ): Promise<ReplayMeta | null> {
    const got = await this.call<{ id: string; meta: ReplayMeta }>('/api/replays', {
      method: 'POST',
      body: JSON.stringify({ replay, ...detail }),
    });
    return got?.meta ?? null;
  }

  async replays(filter: ReplayFilter = {}): Promise<ReplayPage> {
    const got = await this.call<ReplayPage>(`/api/replays${replayQuery(filter)}`);
    return got ?? { replays: [], legends: [], against: [] };
  }

  async replay(id: string): Promise<{ replay: Replay; meta: ReplayMeta } | null> {
    return this.call<{ replay: Replay; meta: ReplayMeta }>(
      `/api/replays/${encodeURIComponent(id)}`,
    );
  }

  /** Turns the share link on or off, returning what it now is. */
  async shareReplay(id: string, shared: boolean): Promise<boolean | null> {
    const got = await this.call<{ shared: boolean }>(
      `/api/replays/${encodeURIComponent(id)}/share`,
      { method: 'POST', body: JSON.stringify({ shared }) },
    );
    return got?.shared ?? null;
  }

  async deleteReplay(id: string): Promise<boolean> {
    return (await this.call<{ ok: true }>(`/api/replays/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    })) !== null;
  }

  /** Records a finished match and returns the updated totals. */
  async record(
    outcome: 'win' | 'loss' | 'draw',
    detail: { deckId?: string; turns?: number; opponent?: string } = {},
  ): Promise<Stats | null> {
    const got = await this.call<{ stats: Stats }>('/api/results', {
      method: 'POST',
      body: JSON.stringify({ outcome, ...detail }),
    });
    return got?.stats ?? null;
  }
}

/** How a finished game reads for one seat. */
export function outcomeFor(
  winner: PlayerId | null, me: PlayerId,
): 'win' | 'loss' | 'draw' {
  if (winner === null) return 'draw';
  return winner === me ? 'win' : 'loss';
}

export const formatRecord = (r: Record): string =>
  `${r.win}W ${r.loss}L${r.draw > 0 ? ` ${r.draw}D` : ''}`;
